import { AxiosRequestConfig } from "axios";

import { moneefloApi } from "./client";

/**
 * Drop-in replacement for Xano's partner-detail call, so the original screen
 * keeps its layout and only its data source changes.
 *
 * A caveat worth reading before trusting this screen. Xano's `channels` table
 * carried about twenty-five fields per partner; the migration brought across
 * nine — name, type, level, parent, email, phone, city, state, status. The
 * rest (GST number, masked PAN and Aadhaar, address lines, pincode, property
 * type, branch type, RM name, stage tags, Zoho contract status, whether
 * credentials were shared) were never copied, and the Xano token has since
 * been rotated, so they cannot be re-pulled from here.
 *
 * Those fields come back as "NA" rather than as blanks or invented values.
 * The screen was built for that — it runs every field through a
 * `getValueOrNA` helper — so it renders honestly and shows exactly how much
 * of a partner record is missing.
 */

type Payload<T> = AxiosRequestConfig<T>;

/** What the migration did not carry. Shown as NA, never guessed. */
const NOT_MIGRATED = "NA";

type OrganizationDetail = {
  id: string;
  name: string;
  type: string;
  partnerLevel: number | null;
  status: string;
  email: string | null;
  phone: string | null;
  city: string | null;
  state: string | null;
  createdAt?: string;
  parent: { id: string; name: string } | null;
  /** The relationship manager, when one has been set. */
  assignedTo?: { id: string; name: string; email?: string } | null;
  /** People linked to it, as far as the caller may read them. */
  contacts?: {
    customerId: string;
    fullName: string;
    phone: string | null;
    email: string | null;
    relationship: string | null;
  }[];
  children: { id: string; name: string; type: string }[];
  childrenTotal?: number;
  users: {
    fullName: string;
    email: string;
    role: string | null;
    status: string;
  }[];
};

export type GetPartnerDetailsRequestType = Partial<{
  /** Both spellings are in use across this screen's two callers. */
  channels_id: string | number;
  channel_id: string | number;
  headers: { Authorization: string };
}>;

/* eslint-disable @typescript-eslint/no-explicit-any */
export async function getPartnerDetails(
  payload: Payload<GetPartnerDetailsRequestType> & {
    params?: { channel_id?: string | number };
  },
  // `any` on purpose: this screen reads dozens of nested Xano fields, and a
  // narrower type here would only move the errors into the screen.
): Promise<{ data: any }> {
  const id =
    payload.data?.channels_id ??
    payload.data?.channel_id ??
    payload.params?.channel_id;
  if (id === undefined) throw new Error("A partner id is required.");

  const [{ data }, interactions] = await Promise.all([
    moneefloApi.get<OrganizationDetail>(`/organizations/${id}`),
    // The page renders without them rather than not at all.
    getCorporateInteractions(id).catch(() => []),
  ]);

  return {
    data: {
      channel_info: {
        id: data.id,
        name: data.name,
        phone: data.phone ?? NOT_MIGRATED,
        email: data.email ?? NOT_MIGRATED,
        status: data.status,
        type: data.type,
        city: data.city ?? NOT_MIGRATED,
        state: data.state ?? NOT_MIGRATED,
        approval_status: data.status === "active" ? "approved" : data.status,
        branch_type: data.partnerLevel ? `L${data.partnerLevel}` : NOT_MIGRATED,
        // getRelativeDate takes epoch milliseconds; an ISO string came out
        // as "5:30 am", the start of the day in IST.
        created_at: data.createdAt ? new Date(data.createdAt).getTime() : null,

        // Never migrated from Xano's channels table.
        pan_type: NOT_MIGRATED,
        masked_pan_no: NOT_MIGRATED,
        masked_aadhar_no: NOT_MIGRATED,
        address_line_1: NOT_MIGRATED,
        address_line_2: NOT_MIGRATED,
        pincode: NOT_MIGRATED,
        property_type: NOT_MIGRATED,
        gst_number: NOT_MIGRATED,
        // The screen reads the manager off `_rm.fname`, as Xano shaped it.
        _rm: data.assignedTo
          ? { id: data.assignedTo.id, fname: data.assignedTo.name }
          : null,
        _rm_name: data.assignedTo?.name ?? NOT_MIGRATED,
        digital_interest: NOT_MIGRATED,
        stage_tags: [],
        credential_shared: NOT_MIGRATED,
        zoho_contract_status: NOT_MIGRATED,
        extra_detail: null,
        note: null,
        channels_id: data.id,
        // One level deeper than the corporate page: each row is read as
        // `interaction[0]`, with the channel at `_channel_of_interaction[0]`.
        interactions_id: interactions.map((item) => [
          {
            id: item.id,
            created_at: new Date(item.occurredAt).getTime(),
            note: item.note ?? "",
            // An object, as Xano sent it; the table maps over a bare
            // string's length and crashes.
            createdBy: item.by ? { fname: item.by } : null,
            contact_with: null,
            _channel_of_interaction: [
              {
                Channel: INTERACTION_LABEL[item.channel] ?? "Other",
                Type: item.direction === "inbound" ? "outbound" : "inbound",
              },
            ],
          },
        ]),
        isPrimary: false,
        relation_with_partner: NOT_MIGRATED,
        linked_partner_id: data.parent ? data.parent.id : null,
        partner_link_time: null,
        checklist_completed: false,

        // Relations Xano nested inside channel_info. Empty is how the screen
        // reads "none on file"; none of these are migrated yet.
        _bank_account_of_channels: [],
        _channel_commission: [],
        _onboarding_checklist: [],
        _created_by: null,
        _partner_linked_by: null,
        products: [],
        docs: [],

        // Documents live in object storage, which has no public address yet.
        // An empty url is how this screen says "no document on file".
        pan: { url: "" },
        aadhar_front: { url: "" },
        aadhar_back: NOT_MIGRATED,
        address_proof: { url: "" },
        selfie: { url: "" },
        photos: [],
      },
      _branch: data.parent
        ? { id: data.parent.id, name: data.parent.name }
        : null,
      // The linked contacts first, as Xano listed them (the first is the one
      // the lead came through), then the partner's own sign-in accounts.
      team_member: [
        ...(data.contacts ?? []).map((contact, index) => ({
          id: contact.customerId,
          name: contact.fullName,
          full_name: contact.fullName,
          phone_number: contact.phone ?? "",
          email: contact.email ?? "",
          role: contact.relationship ?? NOT_MIGRATED,
          relation_with_partner: contact.relationship ?? "",
          isPrimary_partner: index === 0,
          status: "contact",
        })),
        ...data.users.map((user) => ({
          id: user.email,
          name: user.fullName,
          full_name: user.fullName,
          email: user.email,
          role: user.role ?? NOT_MIGRATED,
          status: user.status,
        })),
      ],
      // Onboarding checklists and the reshare flow were Xano workflow state and
      // have no equivalent yet; an empty checklist reads as "nothing pending".
      checklist: [],
      completed: false,
      reshare_button: false,
    },
  };
}

/**
 * The corporate lead screen's fetch. A corporate client is an organization of
 * type `corporate` here, so it reads from the same endpoint as a partner and
 * is reshaped into the flat company object that screen expects.
 */
export type GetSingleCompanyRequestType = Partial<{
  /** The corporate screen sends entity_id; other callers use company_id. */
  entity_id: string | number;
  company_id: string | number;
  headers: { Authorization: string };
}>;

export async function getSingleCompanyLeads(
  payload: Payload<GetSingleCompanyRequestType> & {
    params?: { entity_id?: string | number; company_id?: string | number };
  },
): Promise<{ data: any }> {
  const id =
    payload.params?.entity_id ??
    payload.params?.company_id ??
    payload.data?.entity_id ??
    payload.data?.company_id;
  if (id === undefined) throw new Error("A company id is required.");

  const [{ data }, interactions] = await Promise.all([
    moneefloApi.get<OrganizationDetail>(`/organizations/${id}`),
    // The page renders without them rather than not at all.
    getCorporateInteractions(id).catch(() => []),
  ]);

  return {
    data: {
      id: data.id,
      entity_name: data.name,
      // The page reads the manager as `_rm_id.fname` and the log as
      // `interactions_id`, both in Xano's shape. Neither was filled, so
      // every corporate showed "(Not Available)" and an empty log.
      _rm_id: data.assignedTo
        ? { id: data.assignedTo.id, fname: data.assignedTo.name }
        : null,
      interactions_id: interactions.map((item) => ({
        id: item.id,
        // getRelativeDate takes epoch milliseconds.
        created_at: new Date(item.occurredAt).getTime(),
        note: item.note ?? "",
        // An object, as Xano sent it: the table maps over this when it has
        // a length, so a bare string would crash the page.
        _createdBy: item.by ? { fname: item.by } : null,
        _contactedTo: null,
        _channel_of_interaction: {
          Channel: INTERACTION_LABEL[item.channel] ?? "Other",
          // The table files "I contacted" under `inbound` (see SCREEN_TYPE in
          // legacy-opportunity-api), so the API's plain direction is flipped.
          Type: item.direction === "inbound" ? "outbound" : "inbound",
        },
      })),
      email: data.email ?? NOT_MIGRATED,
      phone: data.phone ?? NOT_MIGRATED,
      city: data.city ?? NOT_MIGRATED,
      state: data.state ?? NOT_MIGRATED,
      status: data.status,
      // getRelativeDate takes epoch milliseconds, not an ISO string.
      created_at: data.createdAt ? new Date(data.createdAt).getTime() : null,
      unique_id: data.id,
      address_id: [],
      extra_detail: null,

      // Xano's corporate record carried these; the migration did not.
      gst_number: NOT_MIGRATED,
      pan_no: NOT_MIGRATED,
      digital_interest: NOT_MIGRATED,
      stage_tags: [],
      _contacts: [],
      // The people linked to this company, in Xano's contact shape: the
      // "Contacted with" picker reads `full_name`, the header the primary.
      contacts_id: (data.contacts ?? []).map((contact, index) => ({
        id: contact.customerId,
        full_name: contact.fullName,
        contactName: contact.fullName,
        phone_number: contact.phone ?? "",
        email: contact.email ?? "",
        relation_with_company: contact.relationship ?? "",
        // The first person linked is the one the lead came through.
        isPrimary_company: index === 0,
      })),
      _company_documents: [],
      team_member: data.users.map(function (user) {
        return {
          id: user.email,
          contactName: user.fullName,
          contactEmail: user.email,
          contactNumber: "",
          role: user.role ?? NOT_MIGRATED,
          status: user.status,
        };
      }),
    },
  };
}

/* ------------------------------------------------------------------ *
 * Corporate account management
 *
 * The corporate lead page could be read but not acted on: reassigning
 * and logging an interaction both still pointed at Xano, and the shared
 * client rejects those before they leave the browser — which is why the
 * network tab stayed empty while a toast said it had failed.
 *
 * These keep the old signatures so the page does not have to change.
 * ------------------------------------------------------------------ */

export type EditCompanyRequest = Partial<{
  coorporate_company_id: string | number;
  staff_id: string | number;
  reminder_time: string;
  reminder_note: string;
  removeReminder: boolean;
  lead_status: string;
  note: string;
  branch: string;
}>;

/**
 * Reassign, and the lead-status dropdown beside it.
 *
 * Reminders and notes are not carried: the API has nowhere to put them
 * yet, and silently dropping them would be worse than saying so. The
 * call still succeeds for the parts that do exist, so reassigning works
 * even while a reminder set at the same time does not.
 */
export async function EditCompany(
  payload: Payload<EditCompanyRequest> & { data?: EditCompanyRequest },
): Promise<{ status: number; data: unknown }> {
  const body = payload.data ?? {};
  const id = body.coorporate_company_id;
  if (id === undefined) throw new Error("A company id is required.");

  const patch: Record<string, unknown> = {};
  if (body.staff_id !== undefined && body.staff_id !== "") {
    patch.assignedUserId = String(body.staff_id);
  }
  if (body.lead_status) patch.status = body.lead_status;

  if (Object.keys(patch).length === 0) {
    // Nothing the API can act on — most likely a reminder-only save.
    return { status: 200, data: { skipped: true } };
  }

  const { data } = await moneefloApi.patch(
    `/organizations/${id}/assignment`,
    patch,
  );
  return { status: 200, data };
}

export type CorpAddInteractionsReqType = Partial<{
  note: string;
  meeting_time: string;
  meetingStaff_id: number[];
  channel_of_interaction_id: string | number;
  contact: string;
  company_id: string | number;
  selfie_image: string;
  visiting_card: string;
  quotation_pdf: string;
  geo_coordinates: { type: string; data: { lng?: string; lat?: string } };
}>;

/** Xano's channel ids, which the dropdown still sends. */
const INTERACTION_CHANNEL: Record<string, string> = {
  "1": "call",
  "2": "email",
  "3": "visit",
  "4": "whatsapp",
  "5": "meeting",
};

/**
 * Records a call or a visit against a corporate client.
 *
 * The selfie and visiting card are not carried yet — they need the
 * document store wiring up — so the note, the channel, the meeting time
 * and the coordinates are kept and the attachments are not. Who logged
 * it comes from the session on the server, not from here.
 */
export async function corpAddInteractions(
  payload: Payload<CorpAddInteractionsReqType> & {
    data?: CorpAddInteractionsReqType;
  },
): Promise<{ status: number; data: unknown }> {
  const body = payload.data ?? {};
  if (body.company_id === undefined) throw new Error("A company id is required.");
  if (!body.note?.trim()) throw new Error("A note is required.");

  const lat = Number(body.geo_coordinates?.data?.lat);
  const lng = Number(body.geo_coordinates?.data?.lng);

  const { data } = await moneefloApi.post(
    `/organizations/${body.company_id}/interactions`,
    {
      note: body.note,
      channel:
        INTERACTION_CHANNEL[String(body.channel_of_interaction_id ?? "")] ??
        "other",
      direction: "outbound",
      ...(body.meeting_time
        ? { meetingAt: new Date(body.meeting_time).toISOString() }
        : {}),
      ...(Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : {}),
    },
  );
  return { status: 200, data };
}

/* ------------------------------------------------ a new corporate lead */

export type PostCorporateLeadReqType = Partial<{
  headers: { Authorization: string };
  name: string;
  type: "SMB" | "enterprise";
  website: string;
  state: string;
  city: string;
  services: string[];
  rm_id: string;
  channel_of_interaction_id: string;
  meeting_staff: string[];
  contacted_with: string;
  meeting_time: string;
  branch: string;
  note: string;
  contacts_id: string;
  relation: string;
  selfie: string;
  visiting_card: string;
}>;

/** The ids the lead screen's channel tabs send, in the API's words. */
const LEAD_CHANNEL: Record<
  string,
  { channel: string; direction: "inbound" | "outbound"; label: string }
> = {
  "1": { channel: "call", direction: "outbound", label: "Call" },
  "2": { channel: "email", direction: "outbound", label: "Email" },
  "3": { channel: "visit", direction: "outbound", label: "Meeting" },
  "13": { channel: "chat", direction: "outbound", label: "Chat" },
  "5": { channel: "call", direction: "inbound", label: "Call from them" },
  "6": { channel: "email", direction: "inbound", label: "Email from them" },
  "7": { channel: "visit", direction: "inbound", label: "They visited" },
};

/**
 * "Create corporate lead", opened from a contact.
 *
 * Xano took this in one call. Here the company is created as a lead, under
 * the branch handling it and with its relationship manager, and the first
 * conversation is logged against it as an interaction.
 *
 * An organization has no field yet for the website, the size, the services
 * wanted or the contact it came through, so those are written into the
 * logged note rather than dropped. The selfie and visiting card are not
 * carried, as corpAddInteractions does not carry them.
 */
export async function postCorporateLead(
  payload: Payload<PostCorporateLeadReqType> & {
    data?: PostCorporateLeadReqType;
  },
): Promise<{ status: number; data: { id: string }; warning?: string }> {
  const body = payload.data ?? {};
  const name = body.name?.trim();
  if (!name) throw new Error("A corporate lead needs a company name.");

  const { data: organization } = await moneefloApi.post<{ id: string }>(
    "/organizations",
    {
      name,
      type: "corporate",
      status: "lead",
      state: body.state?.trim() || undefined,
      city: body.city?.trim() || undefined,
      parentId: body.branch ? String(body.branch) : undefined,
      assignedUserId: body.rm_id ? String(body.rm_id) : undefined,
    },
  );

  const how = LEAD_CHANNEL[String(body.channel_of_interaction_id ?? "")];
  const note = [
    body.note?.trim(),
    body.type ? `Size: ${body.type === "SMB" ? "Small business" : "Enterprise"}` : "",
    body.website?.trim() ? `Website: ${body.website.trim()}` : "",
    body.services?.length ? `Interested in: ${body.services.join(", ")}` : "",
    body.relation?.trim() ? `Contact's role: ${body.relation.trim()}` : "",
  ]
    .filter(Boolean)
    .join("\n");

  // The company exists by now. Failing the whole call here would send the
  // rep back to resubmit, and a second submit is refused as a duplicate name,
  // so the lead stands and the screen is told what did not save.
  const missed: string[] = [];
  if (body.contacts_id) {
    try {
      await linkContact(organization.id, body.contacts_id, body.relation);
    } catch {
      missed.push("the contact was not linked");
    }
  }
  try {
    await moneefloApi.post(`/organizations/${organization.id}/interactions`, {
      note: note || `Lead captured${how ? ` (${how.label})` : ""}`,
      channel: how?.channel ?? "other",
      direction: how?.direction ?? "outbound",
      ...(body.meeting_time && how?.channel === "visit"
        ? { meetingAt: new Date(body.meeting_time).toISOString() }
        : {}),
    });
  } catch {
    missed.push("the first interaction did not save");
  }

  return missed.length
    ? {
        status: 200,
        data: { id: organization.id },
        warning: `Lead created, but ${missed.join(" and ")}. Add it from the lead's page.`,
      }
    : { status: 200, data: { id: organization.id } };
}

/** The words the interaction table shows for each channel. */
const INTERACTION_LABEL: Record<string, string> = {
  call: "Call",
  email: "Email",
  visit: "Meeting",
  meeting: "Meeting",
  chat: "Chat",
  whatsapp: "WhatsApp",
};

/** What has been said to this client, for the Interaction tab. */
export async function getCorporateInteractions(companyId: string | number) {
  const { data } = await moneefloApi.get<{
    items: Array<{
      id: string;
      note: string | null;
      channel: string;
      direction?: string | null;
      occurredAt: string;
      meetingAt: string | null;
      by: string | null;
    }>;
  }>(`/organizations/${companyId}/interactions`);
  return data.items;
}

/* ------------------------------------------- linking a contact to a company */

/**
 * Every corporate or partner the caller can see, for the link dialogs.
 *
 * The dialogs load the whole list once and filter it as the user types, as
 * they did against Xano's UAT list, so this pages through to the end.
 * `role` carries the status the way each dialog reads it: the corporate one
 * shows "Registered" for "Active", the partner one routes on "active".
 */
export async function listOrganizationsForLink(
  type: "corporate" | "partner",
): Promise<
  { id: string; name: string; email: string; address: string; status: string }[]
> {
  const rows: {
    id: string;
    name: string;
    email: string | null;
    city: string | null;
    status: string;
  }[] = [];
  for (let page = 1; page <= 50; page++) {
    const { data } = await moneefloApi.get<{
      items: typeof rows;
      total: number;
    }>("/organizations", { params: { type, page, perPage: 100 } });
    rows.push(...(data.items ?? []));
    if (!data.items?.length || rows.length >= (data.total ?? 0)) break;
  }
  // A row seen twice across pages would give the dialog two list items with
  // the same key, and React then leaves stale rows on screen.
  const seen = new Set<string>();
  return rows
    .filter((org) => !seen.has(String(org.id)) && seen.add(String(org.id)))
    .map((org) => ({
      id: String(org.id),
      name: org.name,
      email: org.email ?? "",
      address: org.city ?? "",
      status: org.status,
    }));
}

/** Links a contact to an organization, with what they are to it. */
async function linkContact(
  organizationId: string | number | undefined,
  customerId: string | number | undefined,
  relationship: string | undefined,
): Promise<{ status: number; data: unknown }> {
  if (organizationId === undefined || organizationId === "") {
    throw new Error("Pick a company first.");
  }
  if (customerId === undefined || customerId === "") {
    throw new Error("Open this from a contact to link them.");
  }
  const { data } = await moneefloApi.post(
    `/organizations/${organizationId}/contacts`,
    {
      customerId: String(customerId),
      ...(relationship ? { relationship } : {}),
    },
  );
  return { status: 200, data };
}

export type PatchLinkCorporateReqType = Partial<{
  headers: { Authorization: string };
  coorporate_company_id: string | number;
  contacts_id: string | number;
  relationship_with_company: string;
}>;

export async function patchLinkCorporate(
  payload: Payload<PatchLinkCorporateReqType> & {
    data?: PatchLinkCorporateReqType;
  },
): Promise<{ status: number; data: unknown }> {
  const body = payload.data ?? {};
  return linkContact(
    body.coorporate_company_id,
    body.contacts_id,
    body.relationship_with_company,
  );
}

export async function patchLinkPartner(
  payload: Payload<{
    partner_id?: string | number;
    contacts_id?: string | number;
    relationship_with_partner?: string;
  }> & {
    data?: {
      partner_id?: string | number;
      contacts_id?: string | number;
      relationship_with_partner?: string;
    };
  },
): Promise<{ status: number; data: unknown }> {
  const body = payload.data ?? {};
  return linkContact(
    body.partner_id,
    body.contacts_id,
    body.relationship_with_partner,
  );
}

/* --------------------------------------------------- a new partner lead */

export type PartnerAcquisitionReqType = Partial<{
  headers: { Authorization: string };
  body: {
    entityName: string;
    type: string;
    city: string;
    state: string;
    rm_id: string | number;
    services: string[];
    channel_of_interaction_id: string | number;
    meeting_time: string | number | null;
    meetingStaff_id: (string | number)[];
    notes: string;
    contact_id: string | number;
    contacts_id: string | number;
    relationship_with_partner: string;
    selfie: string;
    visiting_card: string;
  };
}>;

/**
 * "Create partner lead", opened from a contact.
 *
 * Like the corporate one: the partner is created as a lead with its
 * manager, the contact it came through is linked, and the first
 * conversation is logged. It sits under the caller's own organization,
 * which is where a partner the desk registers always goes; the server
 * works out its level from there. The sub-type and services have no field
 * yet, so they go into the note; the selfie and visiting card are not
 * carried.
 */
export async function partnerAcquisitionOpportunity(
  payload: Payload<PartnerAcquisitionReqType["body"]> & {
    data?: PartnerAcquisitionReqType["body"];
  },
): Promise<{ status: number; data: { id: string; warning?: string } }> {
  const body = payload.data ?? ({} as NonNullable<PartnerAcquisitionReqType["body"]>);
  const name = body.entityName?.trim();
  if (!name) throw new Error("A partner lead needs a name.");

  const rm = body.rm_id !== undefined && body.rm_id !== null ? String(body.rm_id) : "";
  const { data: organization } = await moneefloApi.post<{ id: string }>(
    "/organizations",
    {
      name,
      type: "partner",
      status: "lead",
      state: body.state?.trim() || undefined,
      city: body.city?.trim() || undefined,
      assignedUserId: rm && rm !== "NaN" ? rm : undefined,
    },
  );

  const missed: string[] = [];
  const contact = body.contact_id ?? body.contacts_id;
  if (contact !== undefined && contact !== null && String(contact) !== "") {
    try {
      await linkContact(organization.id, contact, body.relationship_with_partner);
    } catch {
      missed.push("the contact was not linked");
    }
  }

  const how = LEAD_CHANNEL[String(body.channel_of_interaction_id ?? "")];
  const note = [
    body.notes?.trim(),
    body.type ? `Partner type: ${body.type}` : "",
    body.services?.length ? `Interested in: ${body.services.join(", ")}` : "",
    body.relationship_with_partner
      ? `Contact's role: ${body.relationship_with_partner}`
      : "",
  ]
    .filter(Boolean)
    .join("\n");
  try {
    await moneefloApi.post(`/organizations/${organization.id}/interactions`, {
      note: note || `Lead captured${how ? ` (${how.label})` : ""}`,
      channel: how?.channel ?? "other",
      direction: how?.direction ?? "outbound",
      ...(body.meeting_time && how?.channel === "visit"
        ? { meetingAt: new Date(body.meeting_time).toISOString() }
        : {}),
    });
  } catch {
    missed.push("the first interaction did not save");
  }

  return {
    status: 200,
    data: missed.length
      ? {
          id: organization.id,
          warning: `Lead created, but ${missed.join(" and ")}. Add it from the lead's page.`,
        }
      : { id: organization.id },
  };
}
